import cx from 'clsx';
import { motion } from 'motion/react';
import { Suspense, useState } from 'react';

import { ComponentSkeleton } from '#core/components/dashboard/panels/shared/BoxSkeleton.tsx';
import { containerTransition, itemTransition } from '#core/components/dashboard/panels/shared/constants.ts';
import { ErrorBoundaryWrapper } from '#core/components/error/ErrorBoundaryWrapper.tsx';
import { FullscreenToggleButton } from '#core/components/layout/FullscreenToggleButton.tsx';
import type { TileSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import type { DashboardSpec } from '#core/lib/expr/value.ts';

import { CartesianPanel } from './CartesianPanel.tsx';
import classes from './DashboardSpecRenderer.module.css';
import { MetricPanel } from './MetricPanel.tsx';
import { TablePanel } from './TablePanel.tsx';

// biome-ignore lint/suspicious/noExplicitAny: various types
const TILE_COMPONENTS: Record<TileSpec['kind'], React.FC<{ spec: any }>> = {
  'metric-spec': MetricPanel,
  'cartesian-spec': CartesianPanel,
  'table-spec': TablePanel,
};

/** V-shape-native tile dispatch. Exhaustive over `TileSpec.kind`. */
function TileRenderer({ tile }: { tile: TileSpec }) {
  const [fullscreenTarget, setFullscreenTarget] = useState<HTMLDivElement | null>(null);
  const Component = TILE_COMPONENTS[tile.kind] ?? (() => <div>Unknown tile type: {tile.kind}</div>);
  return (
    <motion.div className={cx(classes.gridItem)} variants={itemTransition}>
      <div className={classes.itemContent} ref={setFullscreenTarget}>
        <ErrorBoundaryWrapper
          className={classes.gridItemError}
          context="DashboardGrid.Cell"
          title={`Failed to render ${tile.kind}`}
          variant="inline"
        >
          <Suspense fallback={<ComponentSkeleton type={tile.kind} />}>
            <Component spec={tile} />
          </Suspense>
        </ErrorBoundaryWrapper>
        <FullscreenToggleButton target={fullscreenTarget} />
      </div>
    </motion.div>
  );
}

export function DashboardSpecRenderer({ spec }: { spec: DashboardSpec }) {
  return (
    <ErrorBoundaryWrapper context="DashboardGrid" showRetry title="Dashboard failed to load" variant="alert">
      <div className={classes.gridWrapper}>
        <motion.div
          animate="show"
          className={classes.gridContainer}
          exit="exit"
          initial="hidden"
          variants={containerTransition}
        >
          {spec.tiles.map((tile, i) => (
            <div
              className={classes.cell}
              // biome-ignore lint/suspicious/noArrayIndexKey: tiles are a static declaration-order list with no identity — order + span fully define layout (no reorder/insert)
              key={i}
              style={{ gridColumn: `span ${tile.span[0]}`, gridRow: `span ${tile.span[1]}` }}
            >
              <TileRenderer tile={tile} />
            </div>
          ))}
        </motion.div>
      </div>
    </ErrorBoundaryWrapper>
  );
}
